<template>
    <div
            class="vm-select"
            v-clickoutside="handleClose">
        <div
                class="vm-select__tags"
                v-if="multiple"
                @click.stop="toggleMenu"
                ref="tags"
                :style="{ 'max-width': inputWidth - 32 + 'px' }">
            <transition-group @after-leave="resetInputHeight">
                <vm-tag
                        v-for="item in selected"
                        :key="item.value"
                        :closable="true"
                        :hit="item.hitState"
                        type="primary"
                        @close="deleteTag($event, item)"
                        close-transition>
                    <span class="vm-select__tags-text">{{ item.currentLabel }}</span>
                </vm-tag>
            </transition-group>

            <input
                    type="text"
                    class="vm-select__input"
                    @focus="visible = true"
                    :disabled="disabled"
                    @keyup="managePlaceholder"
                    @keydown="resetInputState"
                    @keydown.down.prevent="navigateOptions('next')"
                    @keydown.up.prevent="navigateOptions('prev')"
                    @keydown.enter.prevent="selectOption"
                    @keydown.esc.stop.prevent="visible = false"
                    @keydown.delete="deletePrevTag"
                    v-model="query"
                    :debounce="remote ? 300 : 0"
                    v-if="filterable"
                    :style="{ width: inputLength + 'px', 'max-width': inputWidth - 42 + 'px', height: inputheight + 'px'}"
                    ref="input">
        </div>
        <vm-input
                ref="reference"
                v-model="selectedLabel"
                type="text"
                :placeholder="currentPlaceholder"
                :name="name"
                :inputclass="`${inputclass} vm-select-input__inner`"
                :disabled="disabled"
                :readonly="!filterable || multiple"
                :validate-event="false"
                @focus="handleFocus"
                @click="handleIconClick"
                @mousedown.native="handleMouseDown"
                @keyup.native="debouncedOnInputChange"
                @keydown.native.down.prevent="navigateOptions('next')"
                @keydown.native.up.prevent="navigateOptions('prev')"
                @keydown.native.enter.prevent="selectOption"
                @keydown.native.esc.stop.prevent="visible = false"
                @keydown.native.tab="visible = false"
                @paste.native="debouncedOnInputChange"
                @mouseenter.native="inputHovering = true"
                @mouseleave.native="inputHovering = false"
                :icon="iconClass">
        </vm-input>
        <transition
                name="vm-zoom-in-top"
                @after-leave="doDestroy"
                @after-enter="handleMenuEnter">
            <vm-select-menu
                    ref="popper"
                    v-show="visible && emptyText !== false">
                <vm-scrollbar
                        tag="ul"
                        wrap-class="vm-select-dropdown__wrap"
                        view-class="vm-select-dropdown__list"
                        :class="{ 'is-empty': !allowCreate && filteredOptionsCount === 0 }"
                        v-show="options.length > 0 && !loading">
                    <vm-option
                            :value="query"
                            created
                            v-if="showNewOption">
                    </vm-option>
                    <slot></slot>
                </vm-scrollbar>
                <p class="vm-select-dropdown__empty" v-if="emptyText && (allowCreate && options.length === 0 || !allowCreate)">{{ emptyText }}</p>
            </vm-select-menu>
        </transition>
    </div>
</template>

<script type="text/babel">
    import Vue from 'vue';
    import Emitter from '../mixins/emitter';
    import Locale from '../mixins/locale';

    import VmInput from 'vue-multiple-input'
    Vue.component(VmInput.name, VmInput)
    import VmScrollbar from 'vue-multiple-scrollbar'
    Vue.component(VmScrollbar.name, VmScrollbar)
    import VmTag from 'vue-multiple-tag'
    Vue.component(VmTag.name, VmTag)

    import VmSelectMenu from './select-dropdown.vue';
    import VmOption from './option.vue';
    import debounce from 'throttle-debounce/debounce';
    import Clickoutside from '../utils/clickoutside';
    import { addClass, removeClass, hasClass } from '../utils/dom';
    import { addResizeListener, removeResizeListener } from '../utils/resize-event';
    import { t } from '../locale';
    const sizeMap = {
        'large': 42,
        'small': 30,
        'mini': 22
    };

    export default {
        mixins: [Emitter, Locale],

        name: 'VmSelect',

        componentName: 'VmSelect',

        computed: {
            iconClass() {
                let criteria = this.clearable &&
                    !this.disabled &&
                    this.inputHovering &&
                    !this.multiple &&
                    this.value !== undefined &&
                    this.value !== '';
                return criteria ? 'vm-select-icon-circle-close show-close' : (this.remote && this.filterable ? '' : 'vm-select-icon-caret-top');
            },

            debounce() {
                return this.remote ? 300 : 0;
            },

            emptyText() {
                if (this.loading) {
                    return this.loadingText || this.t('el.select.loading');
                } else {
                    if (this.remote && this.query === '' && this.options.length === 0) return false;
                    if (this.filterable && this.options.length > 0 && this.filteredOptionsCount === 0) {
                        return this.noMatchText || this.t('el.select.noMatch');
                    }
                    if (this.options.length === 0) {
                        return this.noDataText || this.t('el.select.noData');
                    }
                }
                return null;
            },

            showNewOption() {
                let hasExistingOption = this.options.filter(option => !option.created)
                    .some(option => option.currentLabel === this.query);
                return this.filterable && this.allowCreate && this.query !== '' && !hasExistingOption;
            }
        },

        components: {
            VmSelectMenu,
            VmOption
        },

        directives: { Clickoutside },

        props: {
            name: String,
            value: {
                required: true
            },
            inputclass: {
                type: String,
                default: ''
            },
            inputheight: String,
            disabled: Boolean,
            clearable: Boolean,
            filterable: Boolean,
            allowCreate: Boolean,
            loading: Boolean,
            popperClass: String,
            remote: Boolean,
            loadingText: String,
            noMatchText: String,
            noDataText: String,
            remoteMethod: Function,
            filterMethod: Function,
            multiple: Boolean,
            multipleLimit: {
                type: Number,
                default: 0
            },
            placeholder: {
                type: String,
                default() {
                    return t('el.select.placeholder');
                }
            },
            defaultFirstOption: Boolean
        },

        data() {
            return {
                options: [],
                cachedOptions: [],
                createdLabel: null,
                createdSelected: false,
                selected: this.multiple ? [] : {},
                isSelect: true,
                inputLength: 20,
                inputWidth: 0,
                cachedPlaceHolder: '',
                optionsCount: 0,
                filteredOptionsCount: 0,
                dropdownUl: null,
                visible: false,
                selectedLabel: '',
                hoverIndex: -1,
                query: '',
                bottomOverflow: 0,
                topOverflow: 0,
                optionsAllDisabled: false,
                inputHovering: false,
                currentPlaceholder: ''
            };
        },

        watch: {
            placeholder(val) {
                this.cachedPlaceHolder = this.currentPlaceholder = val;
            },

            value(val) {
                if (this.multiple) {
                    this.resetInputHeight();
                    if (val.length > 0 || (this.$refs.input && this.query !== '')) {
                        this.currentPlaceholder = '';
                    } else {
                        this.currentPlaceholder = this.cachedPlaceHolder;
                    }
                }
                this.setSelected();
                if (this.filterable && !this.multiple) {
                    this.inputLength = 20;
                }
                this.$emit('change', val);
                this.dispatch('VmFormItem', 'el.form.change', val);
            },

            query(val) {
                this.$nextTick(() => {
                    if (this.visible) this.broadcast('VmSelectDropdown', 'updatePopper');
                });
                this.hoverIndex = -1;
                if (this.multiple && this.filterable) {
                    this.inputLength = this.$refs.input.value.length * 15 + 20;
                    this.managePlaceholder();
                    this.resetInputHeight();
                }
                if (this.remote && typeof this.remoteMethod === 'function') {
                    this.hoverIndex = -1;
                    this.remoteMethod(val);
                    this.broadcast('VmOption', 'resetIndex');
                } else if (typeof this.filterMethod === 'function') {
                    this.filterMethod(val);
                    this.broadcast('VmOptionGroup', 'queryChange');
                } else {
                    this.filteredOptionsCount = this.optionsCount;
                    this.broadcast('VmOption', 'queryChange', val);
                    this.broadcast('VmOptionGroup', 'queryChange');
                }
                if (this.defaultFirstOption && (this.filterable || this.remote) && this.filteredOptionsCount) {
                    this.checkDefaultFirstOption();
                }
            },

            visible(val) {
                if (!val) {
                    this.$refs.reference.$el.querySelector('input').blur();
                    this.handleIconHide();
                    this.broadcast('VmSelectDropdown', 'destroyPopper');
                    if (this.$refs.input) {
                        this.$refs.input.blur();
                    }
                    this.query = '';
                    this.selectedLabel = '';
                    this.inputLength = 20;
                    this.resetHoverIndex();
                    this.$nextTick(() => {
                        if (this.$refs.input &&
                            this.$refs.input.value === '' &&
                            this.selected.length === 0) {
                            this.currentPlaceholder = this.cachedPlaceHolder;
                        }
                    });
                    if (!this.multiple) {
                        this.getOverflows();
                        if (this.selected) {
                            if (this.filterable && this.allowCreate &&
                                this.createdSelected && this.createdOption) {
                                this.selectedLabel = this.createdLabel;
                            } else {
                                this.selectedLabel = this.selected.currentLabel;
                            }
                            if (this.filterable) this.query = this.selectedLabel;
                        }
                    }
                } else {
                    this.handleIconShow();
                    this.broadcast('VmSelectDropdown', 'updatePopper');
                    if (this.filterable) {
                        this.query = this.selectedLabel;
                        if (this.multiple) {
                            this.$refs.input.focus();
                        } else {
                            if (!this.remote) {
                                this.broadcast('VmOption', 'queryChange', '');
                                this.broadcast('VmOptionGroup', 'queryChange');
                            }
                            this.broadcast('VmInput', 'inputSelect');
                        }
                    }
                }
                this.$emit('visible-change', val);
            },

            options(val) {
                if (this.$isServer) return;
                this.optionsAllDisabled = val.length === val.filter(item => item.disabled === true).length;
                if (this.multiple) {
                    this.resetInputHeight();
                }
                let inputs = this.$el.querySelectorAll('input');
                if ([].indexOf.call(inputs, document.activeElement) === -1) {
                    this.setSelected();
                }
                if (this.defaultFirstOption && (this.filterable || this.remote) && this.filteredOptionsCount) {
                    this.checkDefaultFirstOption();
                }
            }
        },

        methods: {
            handleIconHide() {
                let icon = this.$el.querySelector('.vm-input__icon');
                if (icon) {
                    removeClass(icon, 'reverse');
                }
            },

            handleIconShow() {
                let icon = this.$el.querySelector('.vm-input__icon');
                if (icon && !hasClass(icon, 'vm-icon-circle-close')) {
                    addClass(icon, 'reverse');
                }
            },

            handleMenuEnter() {
                if (!this.dropdownUl) {
                    this.dropdownUl = this.$refs.popper.$el.querySelector('.vm-select-dropdown__wrap');
                    this.getOverflows();
                }
                if (!this.multiple && this.dropdownUl) {
                    this.resetMenuScroll();
                }
            },

            getOverflows() {
                if (this.dropdownUl && this.selected && this.selected.$el) {
                    let selectedRect = this.selected.$el.getBoundingClientRect();
                    let popperRect = this.$refs.popper.$el.getBoundingClientRect();
                    this.bottomOverflow = selectedRect.bottom - popperRect.bottom;
                    this.topOverflow = selectedRect.top - popperRect.top;
                }
            },

            resetMenuScroll() {
                if (this.bottomOverflow > 0) {
                    this.dropdownUl.scrollTop += this.bottomOverflow;
                } else if (this.topOverflow < 0) {
                    this.dropdownUl.scrollTop += this.topOverflow;
                }
            },

            getOption(value) {
                let option;
                for (let i = this.cachedOptions.length - 1; i >= 0; i--) {
                    const cachedOption = this.cachedOptions[i];
                    if (cachedOption.value === value) {
                        option = cachedOption;
                        break;
                    }
                }
                if (option) return option;
                const label = typeof value === 'string' || typeof value === 'number'
                    ? value : '';
                let newOption = {
                    value: value,
                    currentLabel: label
                };
                if (this.multiple) {
                    newOption.hitState = false;
                }
                return newOption;
            },

            setSelected() {
                if (!this.multiple) {
                    let option = this.getOption(this.value);
                    if (option.created) {
                        this.createdLabel = option.currentLabel;
                        this.createdSelected = true;
                    } else {
                        this.createdSelected = false;
                    }
                    this.selectedLabel = option.currentLabel;
                    this.selected = option;
                    if (this.filterable) this.query = this.selectedLabel;
                    return;
                }
                let result = [];
                if (Array.isArray(this.value)) {
                    this.value.forEach(value => {
                        result.push(this.getOption(value));
                    });
                }
                this.selected = result;
                this.$nextTick(() => {
                    this.resetInputHeight();
                });
            },

            handleFocus() {
                this.visible = true;
            },

            handleIconClick(event) {
                if (this.iconClass.indexOf('circle-close') > -1) {
                    this.deleteSelected(event);
                } else {
                    this.toggleMenu();
                }
            },

            handleMouseDown(event) {
                if (event.target.tagName !== 'INPUT') return;
                if (this.visible) {
                    this.handleClose();
                    event.preventDefault();
                }
            },

            doDestroy() {
                this.$refs.popper && this.$refs.popper.doDestroy();
                this.dropdownUl = null;
            },

            handleClose() {
                this.visible = false;
            },

            toggleLastOptionHitState(hit) {
                if (!Array.isArray(this.selected)) return;
                const option = this.selected[this.selected.length - 1];
                if (!option) return;

                if (hit === true || hit === false) {
                    option.hitState = hit;
                    return hit;
                }

                option.hitState = !option.hitState;
                return option.hitState;
            },

            deletePrevTag(e) {
                if (e.target.value.length <= 0 && !this.toggleLastOptionHitState()) {
                    const value = this.value.slice();
                    value.pop();
                    this.$emit('input', value);
                }
            },

            managePlaceholder() {
                if (this.currentPlaceholder !== '') {
                    this.currentPlaceholder = this.$refs.input.value ? '' : this.cachedPlaceHolder;
                }
            },

            resetInputState(e) {
                if (e.keyCode !== 8) this.toggleLastOptionHitState(false);
                this.inputLength = this.$refs.input.value.length * 15 + 20;
                this.resetInputHeight();
            },

            resetInputHeight() {
                this.$nextTick(() => {
                    if (!this.$refs.reference) return;
                    let inputChildNodes = this.$refs.reference.$el.childNodes;
                    let input = [].filter.call(inputChildNodes, item => item.tagName === 'INPUT')[0];
                    input.style.height = Math.max(this.$refs.tags.clientHeight + 6, this.inputheight||36) + 'px';
                    if (this.visible && this.emptyText !== false) {
                        this.broadcast('VmSelectDropdown', 'updatePopper');
                    }
                });
            },

            resetHoverIndex() {
                setTimeout(() => {
                    if (!this.multiple) {
                        this.hoverIndex = this.options.indexOf(this.selected);
                    } else {
                        if (this.selected.length > 0) {
                            this.hoverIndex = Math.min.apply(null, this.selected.map(item => this.options.indexOf(item)));
                        } else {
                            this.hoverIndex = -1;
                        }
                    }
                }, 300);
            },

            handleOptionSelect(option) {
                if (this.multiple) {
                    const value = this.value.slice();
                    const optionIndex = value.indexOf(option.value);
                    if (optionIndex > -1) {
                        value.splice(optionIndex, 1);
                    } else if (this.multipleLimit <= 0 || value.length < this.multipleLimit) {
                        value.push(option.value);
                    }
                    this.$emit('input', value);
                    if (option.created) {
                        this.query = '';
                        this.inputLength = 20;
                    }
                    if (this.filterable) this.$refs.input.focus();
                } else {
                    this.$emit('input', option.value);
                    this.visible = false;
                }
            },

            toggleMenu() {
                if (this.filterable && this.query === '' && this.visible) {
                    return;
                }
                if (!this.disabled) {
                    this.visible = !this.visible;
                }
            },

            navigateOptions(direction) {
                if (!this.visible) {
                    this.visible = true;
                    return;
                }
                if (this.options.length === 0 || this.filteredOptionsCount === 0) return;
                this.optionsAllDisabled = this.options.length === this.options.filter(item => item.disabled === true).length;
                if (!this.optionsAllDisabled) {
                    if (direction === 'next') {
                        this.hoverIndex++;
                        if (this.hoverIndex === this.options.length) {
                            this.hoverIndex = 0;
                        }
                        this.resetScrollTop();
                        if (this.options[this.hoverIndex].disabled === true ||
                            this.options[this.hoverIndex].groupDisabled === true ||
                            !this.options[this.hoverIndex].visible) {
                            this.navigateOptions('next');
                        }
                    }
                    if (direction === 'prev') {
                        this.hoverIndex--;
                        if (this.hoverIndex < 0) {
                            this.hoverIndex = this.options.length - 1;
                        }
                        this.resetScrollTop();
                        if (this.options[this.hoverIndex].disabled === true ||
                            this.options[this.hoverIndex].groupDisabled === true ||
                            !this.options[this.hoverIndex].visible) {
                            this.navigateOptions('prev');
                        }
                    }
                }
            },

            resetScrollTop() {
                let bottomOverflowDistance = this.options[this.hoverIndex].$el.getBoundingClientRect().bottom -
                    this.$refs.popper.$el.getBoundingClientRect().bottom;
                let topOverflowDistance = this.options[this.hoverIndex].$el.getBoundingClientRect().top -
                    this.$refs.popper.$el.getBoundingClientRect().top;
                if (bottomOverflowDistance > 0) {
                    this.dropdownUl.scrollTop += bottomOverflowDistance;
                }
                if (topOverflowDistance < 0) {
                    this.dropdownUl.scrollTop += topOverflowDistance;
                }
            },

            selectOption() {
                if (this.options[this.hoverIndex]) {
                    this.handleOptionSelect(this.options[this.hoverIndex]);
                }
            },

            deleteSelected(event) {
                event.stopPropagation();
                this.$emit('input', '');
                this.visible = false;
                this.$emit('clear');
            },

            deleteTag(event, tag) {
                let index = this.selected.indexOf(tag);
                if (index > -1 && !this.disabled) {
                    const value = this.value.slice();
                    value.splice(index, 1);
                    this.$emit('input', value);
                    this.$emit('remove-tag', tag);
                }
                event.stopPropagation();
            },

            onInputChange() {
                if (this.filterable) {
                    this.query = this.selectedLabel;
                }
            },

            onOptionDestroy(option) {
                this.optionsCount--;
                this.filteredOptionsCount--;
                let index = this.options.indexOf(option);
                if (index > -1) {
                    this.options.splice(index, 1);
                }
                this.broadcast('VmOption', 'resetIndex');
            },

            resetInputWidth() {
                this.inputWidth = this.$refs.reference.$el.getBoundingClientRect().width;
            },

            handleResize() {
                this.resetInputWidth();
                if (this.multiple) this.resetInputHeight();
            },

            checkDefaultFirstOption() {
                this.hoverIndex = -1;
                for (let i = 0; i !== this.options.length; ++i) {
                    const option = this.options[i];
                    if (this.query) {
                        // pick first options that passes the filter
                        if (!option.disabled && !option.groupDisabled && option.visible) {
                            this.hoverIndex = i;
                            break;
                        }
                    } else {
                        // pick currently selected option
                        if (option.itemSelected) {
                            this.hoverIndex = i;
                            break;
                        }
                    }
                }
            }
        },

        created() {
            this.cachedPlaceHolder = this.currentPlaceholder = this.placeholder;
            if (this.multiple && !Array.isArray(this.value)) {
                this.$emit('input', []);
            }
            if (!this.multiple && Array.isArray(this.value)) {
                this.$emit('input', '');
            }
            this.setSelected();

            this.debouncedOnInputChange = debounce(this.debounce, () => {
                this.onInputChange();
            });

            this.$on('handleOptionClick', this.handleOptionSelect);
            this.$on('onOptionDestroy', this.onOptionDestroy);
            this.$on('setSelected', this.setSelected);
        },

        mounted() {
            if (this.multiple && Array.isArray(this.value) && this.value.length > 0) {
                this.currentPlaceholder = '';
            }
            addResizeListener(this.$el, this.handleResize);
            if (this.remote && this.multiple) {
                this.resetInputHeight();
            }
            this.$nextTick(() => {
                if (this.$refs.reference && this.$refs.reference.$el) {
                    this.inputWidth = this.$refs.reference.$el.getBoundingClientRect().width;
                }
            });
        },

        beforeDestroy() {
            if (this.$el && this.handleResize) removeResizeListener(this.$el, this.handleResize);
        }
    };
</script>



// WEBPACK FOOTER //
// Select.vue?6032f195